import { useEffect, useRef, useState } from 'react'
import type { Submission } from '@/types'
import { tokenizeHashtags } from '@/lib/hashtags'
import { validateNoteText } from '@/lib/validators'
import { Modal } from '@/components/ui/Modal/Modal'
import { useToast } from '@/components/providers/ToastProvider'
import { getDisplayDate } from '@/components/RecordingItem/formatters'
import { updateNoteBody } from '@/lib/api'

interface NoteModalProps {
  isOpen: boolean
  onClose: () => void
  submissionId: string
  noteBody: string
  title: string
  createdAt: string
  noteBodyEditedAt?: string | null
  /** Called with the server's updated submission after a successful save. */
  onSaved: (updated: Submission) => void
  /** When false, the note is view-only (no Edit button). Used once the asset's
   *  active period closes — editing is also rejected server-side. */
  canEdit?: boolean
}

// Matches the recorder's note cap and the server-side `NoteUpdate` limit.
const MAX_NOTE_CHARACTERS = 1000

const PRIMARY_BUTTON =
  'px-4 py-2 rounded-lg bg-neon-cyan text-navy-dark font-semibold hover:bg-neon-cyan/80 transition-colors focus:outline-none focus:ring-2 focus:ring-neon-cyan disabled:opacity-50 disabled:cursor-not-allowed'
const SECONDARY_BUTTON =
  'px-4 py-2 rounded-lg bg-gray-600/20 text-gray-300 hover:bg-gray-600/30 transition-colors focus:outline-none focus:ring-2 focus:ring-neon-cyan disabled:opacity-50 disabled:cursor-not-allowed'

/**
 * Shows a text note and lets the owner edit it. Editing is online-only (the note
 * lives server-side); on save it calls the API and hands the updated submission
 * back via `onSaved`. View mode highlights hashtags without altering the text.
 */
export function NoteModal({
  isOpen,
  onClose,
  submissionId,
  noteBody,
  title,
  createdAt,
  noteBodyEditedAt,
  onSaved,
  canEdit = true,
}: NoteModalProps) {
  const { showToast } = useToast()
  // `value`/`editedAt` are the saved state shown in view mode; `draft` is the
  // edit buffer. Both reset whenever the modal (re)opens.
  const [value, setValue] = useState(noteBody)
  const [editedAt, setEditedAt] = useState<string | null>(noteBodyEditedAt ?? null)
  const [draft, setDraft] = useState(noteBody)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!isOpen) return
    setValue(noteBody)
    setEditedAt(noteBodyEditedAt ?? null)
    setDraft(noteBody)
    setIsEditing(false)
    setIsSaving(false)
  }, [isOpen, noteBody, noteBodyEditedAt])

  useEffect(() => {
    if (isEditing) textareaRef.current?.focus()
  }, [isEditing])

  const trimmed = draft.trim()
  const isEmpty = trimmed.length === 0
  const isUnchanged = trimmed === value.trim()
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
  const validation = validateNoteText(trimmed)
  const canSave =
    !isSaving && !isEmpty && !isUnchanged && !isOffline && validation.valid

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const handleSave = async () => {
    if (!canSave) {
      if (isOffline) showToast('You are offline. Reconnect to save changes.', 'warning')
      else if (isEmpty) showToast('Note cannot be empty', 'warning')
      else if (!validation.valid) showToast(validation.errors[0], 'warning')
      return
    }
    setIsSaving(true)
    try {
      const updated = await updateNoteBody(submissionId, trimmed)
      setValue(updated.noteBody ?? trimmed)
      setEditedAt(updated.noteBodyEditedAt ?? null)
      setIsEditing(false)
      onSaved(updated)
      showToast('Note updated', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to update note', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value
    if (next.length <= MAX_NOTE_CHARACTERS) setDraft(next)
  }

  const footer = isEditing ? (
    <>
      <button
        type="button"
        onClick={() => {
          setDraft(value)
          setIsEditing(false)
        }}
        disabled={isSaving}
        className={SECONDARY_BUTTON}
      >
        Cancel
      </button>
      <button type="button" onClick={handleSave} disabled={!canSave} className={PRIMARY_BUTTON}>
        {isSaving ? 'Saving…' : 'Save'}
      </button>
    </>
  ) : (
    <>
      {canEdit && (
        <button type="button" onClick={() => setIsEditing(true)} className={PRIMARY_BUTTON}>
          Edit
        </button>
      )}
      <button type="button" onClick={handleClose} className={SECONDARY_BUTTON}>
        Close
      </button>
    </>
  )

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} subtitle={createdAt} footer={footer}>
      {isEditing ? (
        <div>
          <label htmlFor="note-editor" className="sr-only">
            Edit note
          </label>
          <textarea
            id="note-editor"
            ref={textareaRef}
            value={draft}
            onChange={handleChange}
            rows={8}
            maxLength={MAX_NOTE_CHARACTERS}
            aria-describedby="note-counter"
            className="w-full rounded-lg bg-navy-dark border border-navy-light p-3 text-sm text-gray-200 leading-relaxed focus:outline-none focus:ring-2 focus:ring-neon-cyan"
          />
          <div className="mt-1 flex items-center justify-between gap-2">
            {isOffline ? (
              <span className="text-xs text-yellow-400" role="alert">
                Offline — reconnect to save.
              </span>
            ) : !validation.valid && !isEmpty ? (
              <span className="text-xs text-yellow-400" role="alert">
                {validation.errors[0]}
              </span>
            ) : (
              <span />
            )}
            <span
              id="note-counter"
              className={`text-xs ${
                MAX_NOTE_CHARACTERS - draft.length < 100 ? 'text-yellow-400' : 'text-gray-400'
              }`}
            >
              {draft.length}/{MAX_NOTE_CHARACTERS}
            </span>
          </div>
        </div>
      ) : (
        <div>
          {editedAt && (
            <p className="mb-2 text-xs italic text-gray-400">Edited · {getDisplayDate(editedAt)}</p>
          )}
          <p className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">
            {tokenizeHashtags(value).map((segment, index) =>
              segment.isHashtag ? (
                <span key={index} className="font-medium text-neon-cyan">
                  {segment.text}
                </span>
              ) : (
                <span key={index}>{segment.text}</span>
              ),
            )}
          </p>
        </div>
      )}
    </Modal>
  )
}
